/**
 * Intercom Integration Utility
 * Based on humblytics-web implementation
 */

// Extend Window interface for Intercom
declare global {
  interface Window {
    Intercom: any
    intercomSettings: any
  }
}

interface IntercomSettings {
  api_base: string
  app_id: string
  user_id?: string
  email?: string
  hide_default_launcher?: boolean
}

/**
 * Load Intercom widget script.
 * If the script is already loaded, syncs the launcher visibility state.
 *
 * @param userEmail - User's email address
 * @param userId - User's ID
 * @param hideLauncher - Whether to hide the floating launcher button
 */
export const loadIntercom = (
  userEmail?: string,
  userId?: string,
  hideLauncher = false
): void => {
  if (typeof window === 'undefined') return

  // If script already exists, just sync launcher state
  if (document.getElementById('intercom-script')) {
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: hideLauncher })
    }
    return
  }

  // Set up Intercom settings — including initial launcher visibility
  window.intercomSettings = {
    api_base: 'https://api-iam.intercom.io',
    app_id: 'mf07go57',
    user_id: userId,
    email: userEmail,
    hide_default_launcher: hideLauncher,
  }

  // Load Intercom script
  ;(function () {
    const w = window as any
    const ic = w.Intercom
    if (typeof ic === 'function') {
      ic('reattach_activator')
      ic('update', w.intercomSettings)
    } else {
      const d = document
      const i: any = function (...args: any[]) {
        i.c(args)
      }
      i.q = []
      i.c = function (args: any) {
        i.q.push(args)
      }
      w.Intercom = i
      const l = function () {
        const s = d.createElement('script')
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://widget.intercom.io/widget/mf07go57'
        s.id = 'intercom-script'
        const x = d.getElementsByTagName('script')[0]
        x.parentNode?.insertBefore(s, x)
      }
      if (document.readyState === 'complete') {
        l()
      } else if (w.attachEvent) {
        w.attachEvent('onload', l)
      } else {
        w.addEventListener('load', l, false)
      }
    }
  })()
}

/**
 * Show Intercom chat widget
 */
export const showIntercom = (): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('show')
  }
}

/**
 * Hide Intercom chat widget
 */
const hideIntercom = (): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('hide')
  }
}

/**
 * Hide the Intercom launcher (the floating button)
 */
export const hideIntercomLauncher = (): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('update', { hide_default_launcher: true })
  }
}

/**
 * Show the Intercom launcher (the floating button)
 */
export const showIntercomLauncher = (): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('update', { hide_default_launcher: false })
  }
}

/**
 * Update Intercom user data
 */
const updateIntercom = (data: Partial<IntercomSettings>): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('update', data)
  }
}

/**
 * Shutdown Intercom
 */
const shutdownIntercom = (): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('shutdown')
  }
}
